import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from './SessionContext';
import { getMembershipTierLabel } from './membership';
import { useOrganisationSubscriptionTier } from './useOrganisationSubscriptionTier';

const PLAN_CARDS = [
  {
    key: 'calendar',
    title: 'Calendar Booking',
    price: 'Free',
    summary: 'Aircraft booking and day-to-day flying organisation.',
    features: [
      'Aircraft booking calendar',
      'Unlimited aircraft',
      'Member access',
      'Operations Board',
      'My Bookings',
      'Pre-flight',
      'Profile',
    ],
  },
  {
    key: 'calendar-logbook-techlog',
    title: 'Calendar + Logbooks',
    price: '£19.99 per month',
    summary: 'Adds pilot records and aircraft operational tools.',
    features: [
      'Everything in Calendar Booking',
      'Weight & Balance',
      'Pilot Logbook',
      'Aircraft Technical Log',
      'Documents',
      'Includes up to 20 active members',
      'Additional active members: £0.50 each per month',
    ],
  },
  {
    key: 'full-package',
    title: 'Full Package',
    price: '£29.99 per month',
    summary: 'Complete club management, administration and payment tools.',
    features: [
      'Everything in Calendar + Logbooks',
      'Advanced administration',
      'Club billing tools',
      'Payment tracking',
      'Includes up to 20 active members',
      'Additional active members: £0.50 each per month',
    ],
  },
];

const FEATURE_LABELS = {
  'pilot-logbook': 'Pilot Logbook',
  'aircraft-tech-log': 'Aircraft Technical Log',
  documents: 'Documents',
  admin: 'Admin and Payments',
};

export default function UpgradePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = useSession();
  const {
    organisationSubscriptionTier,
    organisationSubscriptionLoading,
  } = useOrganisationSubscriptionTier(session?.user);

  const params = new URLSearchParams(location.search || '');
  const requestedFeature = String(params.get('feature') || '').trim();

  const requestedLabel =
    FEATURE_LABELS[requestedFeature] ||
    requestedFeature ||
    'this feature';

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f4f6f8',
        padding: '1.5rem',
      }}
    >
      <div
        style={{
          maxWidth: 1120,
          margin: '0 auto',
          background: '#ffffff',
          border: '1px solid #dbe1e7',
          borderRadius: 12,
          padding: '1.4rem',
          boxShadow: '0 2px 10px rgba(15, 23, 42, 0.06)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
            flexWrap: 'wrap',
            marginBottom: '1.2rem',
          }}
        >
          <div>
            <h1
              style={{
                margin: 0,
                color: '#1f2937',
                fontSize: '1.45rem',
                fontWeight: 650,
              }}
            >
              Wingit plans
            </h1>

            <div
              style={{
                marginTop: '0.35rem',
                color: '#6b7280',
                fontSize: '0.88rem',
              }}
            >
              Current plan:{' '}
              <strong style={{ color: '#374151' }}>
                {organisationSubscriptionLoading
                  ? 'Loading...'
                  : getMembershipTierLabel(
                      organisationSubscriptionTier
                    )}
              </strong>
            </div>
          </div>

          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            style={{
              background: '#ffffff',
              color: '#374151',
              border: '1px solid #cbd5e1',
              borderRadius: 7,
              padding: '0.48rem 0.8rem',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Back
          </button>
        </div>

        {requestedFeature ? (
          <div
            style={{
              marginBottom: '1rem',
              padding: '0.7rem 0.8rem',
              background: '#f8fafc',
              border: '1px solid #dbe4ed',
              borderRadius: 8,
              color: '#475569',
              fontSize: '0.84rem',
              lineHeight: 1.4,
            }}
          >
            {requestedLabel} is available on a higher Wingit plan.
          </div>
        ) : null}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '0.85rem',
          }}
        >
          {PLAN_CARDS.map((plan) => {
            const active =
              !organisationSubscriptionLoading &&
              plan.key === organisationSubscriptionTier;

            return (
              <div
                key={plan.key}
                style={{
                  position: 'relative',
                  border: active
                    ? '1px solid #38bdf8'
                    : '1px solid #dbe1e7',
                  borderRadius: 10,
                  padding: '1rem',
                  background: active ? '#f0f9ff' : '#ffffff',
                  boxShadow: active
                    ? '0 2px 8px rgba(14, 165, 233, 0.1)'
                    : 'none',
                }}
              >
                {active ? (
                  <div
                    style={{
                      display: 'inline-block',
                      marginBottom: '0.55rem',
                      padding: '0.2rem 0.48rem',
                      borderRadius: 999,
                      background: '#dff4ff',
                      color: '#036b91',
                      fontSize: '0.68rem',
                      fontWeight: 700,
                    }}
                  >
                    Current plan
                  </div>
                ) : null}

                <div
                  style={{
                    color: '#1f2937',
                    fontWeight: 650,
                    fontSize: '1rem',
                    marginBottom: '0.25rem',
                  }}
                >
                  {plan.title}
                </div>

                <div
                  style={{
                    color: '#0f6f93',
                    fontWeight: 650,
                    fontSize: '0.92rem',
                    marginBottom: '0.6rem',
                  }}
                >
                  {plan.price}
                </div>

                <div
                  style={{
                    color: '#64748b',
                    fontSize: '0.8rem',
                    lineHeight: 1.45,
                    marginBottom: '0.7rem',
                    minHeight: '2.3rem',
                  }}
                >
                  {plan.summary}
                </div>

                <ul
                  style={{
                    margin: 0,
                    paddingLeft: '1.05rem',
                    color: '#475569',
                    fontSize: '0.78rem',
                    lineHeight: 1.55,
                  }}
                >
                  {plan.features.map((item) => (
                    <li key={item} style={{ marginBottom: '0.15rem' }}>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div
          style={{
            marginTop: '1rem',
            padding: '0.7rem 0.8rem',
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: 8,
            color: '#64748b',
            fontSize: '0.78rem',
            lineHeight: 1.4,
          }}
        >
          New organisations receive a 30-day Full Package trial.
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.6rem',
            marginTop: '1rem',
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={() => navigate('/profile')}
            style={{
              background: '#ffffff',
              color: '#374151',
              border: '1px solid #cbd5e1',
              borderRadius: 7,
              padding: '0.48rem 0.8rem',
              fontSize: '0.82rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
}